import { defineStore } from 'pinia';
import { reactive, ref, watch } from 'vue';
import { useRouter } from 'vue-router';
import * as yup from 'yup';

import { addonItems, totals } from '@/data';
import type { APIReceipt, BasketData, Cardfee, CartItem, CustomField, CustomFieldData, CustomFieldOrderData, CustomFieldOrderDataEntry, FieldListItem, Order, OrderData, PassbookData,ReceiptMessage, SimpleBasketData, StartPurchaseData, Ticket, Totals, UnpaidOrder, DifferentCountMessage } from '@/types';
import { getEventtime } from '@/utils/eventItemUtil';
import { eventToCartItem } from '@/utils/eventToCartItem';
import { formatDate } from '@/utils/formatDate';
import { photoToUrl } from '@/utils/photoToUrl';
import { useWebsocketStore } from '@/websocket/websocket';

import { useCheckoutStore } from './checkoutStore';
import { useGeneralStore } from './general';

export const useCartStore = defineStore('cart', () => {
  const websocketStore = useWebsocketStore();
  let basketData: BasketData = {
    basket: [],
    cardtypes: [],
    dankort_discount: 0,
    dankort_discount_max: 0,
    dankort_discount_min: 0,
    error: '',
    different_count_message: undefined,
    expire: 0,
    volume_discounts_in_use: undefined,
    customfields: undefined,
    timeout: undefined,
  };
  const cartExpire = ref<number>(0);
  let cartExpireInterval: number | undefined = undefined;

  const emptyPassbookData: PassbookData = {
    link: '',
    qr: '',
    atpblink: '',
  };

  const emptyReceiptMessage: ReceiptMessage = {
    cardnumber: '',
    currency: '',
    fee: 0,
    giftcards: [],
    mobile_pay_info: '',
    ok: false,
    orderid: 0,
    transaction: '',
    ts: 0,
    ts_gmt_offset: 0,
    passbook: emptyPassbookData,
  };

  const emptyReceiptOrder: Order = {
      status: '',
      orderid: 0,
      ts: 0,
      ts_gmt_offset: 0,
      mailid: '',
      basket: [],
      info: {} as OrderData,
      payment_id: '',
      payment_vars: {},
      dankort_discount: 0,
      giftcards: [],
      additional_receipt_message: '',
  };

  const emptyOrderData: OrderData = {
    email: '',
    email2: '',
    name: '',
    address: '',
    address2: '',
    zip: '',
    city: '',
    telephone: '',
    country: '',
    newsletter: false,
    customcheckbox: false,
    vouchercodesavedata: false,
    mailshipping: false,
    giftcards: [], // change later
    price_group_info: undefined, // change later
    customfields_data: undefined, // change later
  };

  const emptyCustomFieldData: CustomFieldData = {
        custom_fields: [],
        custom_fields_perticket: [],
        custom_fields_pertickettype: [],
        fieldlists: {},
  };

  const emptyCardfee: Cardfee = {
    dankort_discount: null,
    min: 0,
    max: 0,
    mintype: '',
  };

  const emptyUnpaidOrder: UnpaidOrder = {
    cardfee: emptyCardfee,
    order: emptyReceiptOrder,
    psp: '',
    psp_warning: '',
  };

  const cartTotals = ref<Totals>(totals);
  const cardTypes = ref<string[]>([]);
  const receiptCart = ref<boolean>(false);
  const paymentConfirmed = ref<boolean>(false);
  const pdfReady = ref<boolean>(false);
  const receiptOrder = ref<Order>(emptyReceiptOrder);
  const receiptMessage = ref<ReceiptMessage>(emptyReceiptMessage);
  const receiptMessagesSent = ref<Record<string, boolean>>({});
  const pdfLink = ref<string>('#');
  const customfields = ref<CustomFieldData>(emptyCustomFieldData);

  const generalStore = useGeneralStore();
  const checkoutStore = useCheckoutStore();
  const items: CartItem[] = reactive(basketData.basket);
  const currentOrderData = ref<OrderData>(emptyOrderData);
  const customFieldRules = ref<Record<string, yup.AnySchema | undefined>>({});
  const currentCount = ref<Record<string, number>>({});
  const differentCount = ref<boolean>(false);
  const differentCountCalculating = ref<boolean>(false);
  const differentCountMessage = ref<DifferentCountMessage>({ message: 'initial', count: 0 });
  const unpaidOrder = ref<UnpaidOrder>(emptyUnpaidOrder);
  const unpaidOrderBackup = ref<UnpaidOrder>(emptyUnpaidOrder);
  const paymentFailedTimeoutFlag = ref<boolean>(false);
  const ignoreCancelPaymentModal = ref<boolean>(false);
  
  const paymentFailedTimeout = ref<number | undefined>(undefined);
  const paymentFailedExpire = ref<number>(0);
  const paymentFailedCart = ref<boolean>(false);
  const paymentFailedAvailable = ref<boolean>(false);
  const firstBasketLoad = ref<boolean>(true);

  function doBackupUnpaidOrder() {
    if( unpaidOrderBackup.value.order && unpaidOrderBackup.value.order.ts && unpaidOrderBackup.value.order.ts > 0 ) {
      const now = Date.now() / 1000;
      const orderExpireTime = unpaidOrderBackup.value.order.ts + (8 * 60);
      if( orderExpireTime > now ) {
        setUnpaidOrder(unpaidOrderBackup.value);
      }
      else {
        doHardLeaveUnpaidOrder();
      }
    }
  }

  function setUnpaidOrder(data: UnpaidOrder) {
    //If we are not in checkout-payment, we do some cleanup and return
    if(router.currentRoute.value.name != 'checkout-payment' && router.currentRoute.value.name != 'checkout-information') {
      console.log('Not in checkout-payment or checkout-information, clearing unpaid order data and returning');
      paymentFailedAvailable.value = false;
      unpaidOrderBackup.value = emptyUnpaidOrder;
      clearUnpaidOrderData();
      return;
    }
    else if( data.order && data.order.ts){
      console.log('Setting unpaid order with timestamp:', data.order.ts);
      const now = Date.now() / 1000;
      const orderExpireTime = data.order.ts + (8 * 60);
      //If the order is already expired we go to the frontpage and clear cart and unpaid order data
      if( orderExpireTime < now ) {
        doHardLeaveUnpaidOrder();
      }
      else if(router.currentRoute.value.name == 'checkout-information'){
        //This usually happens when the user clicks the back button in their broswer from payment. So here we wish to set a flag.
        //The above if ensures the order is valid
        paymentFailedAvailable.value = true;
        unpaidOrderBackup.value = data;
        clearUnpaidOrderData();
        return;
      }
    }


    unpaidOrder.value = data;
    console.log('Unpaid order set:', unpaidOrder.value);
    if( data.order && data.order.orderid && data.order.status && data.order.status === 'placed' ) {
      checkoutStore.placeOrderPaymentVars = data.order.payment_vars;
    }
    // Set unpaid order timeout
    if(data.order && data.order.basket && data.order.basket.some(item => item.tickets.some(ticket => ticket.count > 0)) ) {
      console.log('Current time:', Date.now() / 1000);
      console.log('Unpaid order data:', data);


      basketData.basket = unpaidOrder.value.order.basket;
      paymentFailedCart.value = true;
      addEventDataToBasket();
      items.splice(0, items.length, ...basketData.basket);
      console.log('Items after update:', items);
      calculateTotals();

      paymentFailedExpire.value = data.order.ts + (8 * 60);
      console.log('Payment failed expire time set to:', paymentFailedExpire.value, 'which is', new Date(paymentFailedExpire.value * 1000).toLocaleString());
      paymentFailedTimeout.value = window.setInterval(() => {
        const now = Date.now() / 1000;
        const orderExpireTime = paymentFailedExpire.value;
        if (orderExpireTime && orderExpireTime > 0 && now > orderExpireTime) {
          doUnpaidOrderTimeout();
        }
      }, 1000);
    }
    else {
      paymentFailedTimeoutFlag.value = false;
      if( paymentFailedTimeout.value ) {
        clearInterval(paymentFailedTimeout.value);
        paymentFailedTimeout.value = undefined;
      }
    }

    //We shouldn't stay here if do not have an unpaid order or any valid as a backup
    if( 
      router.currentRoute.value.name == 'checkout-payment' &&
      unpaidOrder.value.order == null &&
      !(unpaidOrderBackup.value.order && unpaidOrderBackup.value.order.ts && unpaidOrderBackup.value.order.ts > 0)
    ){
      doHardLeaveUnpaidOrder();
    }

  }


  function doHardLeaveUnpaidOrder() {
    console.log('Unpaid order expired, clearing unpaid order data and redirecting to frontpage');
    localStorage.setItem('showUnpaidOrderExpiredToast', 'true');
    resetCart();
    clearUnpaidOrderData(false);
    ignoreCancelPaymentModal.value = true;
    paymentFailedAvailable.value = false;
    paymentFailedCart.value = false;
    unpaidOrderBackup.value = emptyUnpaidOrder;
    router.push('/');
  }

  function doUnpaidOrderTimeout() {
    console.log('Unpaid order expired, resetting cart and unpaid order data');
    resetCart();
    clearUnpaidOrderData(true);
  }

  function clearUnpaidOrderData(timeout: boolean = false) {
    paymentFailedTimeoutFlag.value = timeout;
    clearInterval(paymentFailedTimeout.value);
    paymentFailedTimeout.value = undefined;
    paymentFailedExpire.value = 0;
    paymentFailedCart.value = false;
    unpaidOrder.value = emptyUnpaidOrder;
    checkoutStore.placeOrderPaymentVars = null;

  }

  function setCustomFieldRule(fieldName: string, rule: yup.AnySchema | undefined) {
    customFieldRules.value[fieldName] = rule;
    // console.log('Custom field rule set for', fieldName, ':', rule);
  }

  function getCustomFieldRule(fieldName: string) {
    // console.log('Getting custom field rule for', fieldName);
    // console.log('Current custom field rules:', customFieldRules.value);
    return customFieldRules.value[fieldName];
  }

  function setCustomFieldData(basket: BasketData) {
    const data = basket.customfields as CustomFieldData | undefined;
    // console.log('Setting custom field data:', data);
    if( data ) {
      if(data.custom_fields != undefined){
        customfields.value.custom_fields = data.custom_fields;
      }
      if(data.custom_fields_perticket != undefined){
        customfields.value.custom_fields_perticket = data.custom_fields_perticket;
      }
      if(data.custom_fields_pertickettype != undefined){
        customfields.value.custom_fields_pertickettype = data.custom_fields_pertickettype;
      }
      if(data.fieldlists != undefined){
        customfields.value.fieldlists = data.fieldlists;
      }
      const tempOrderData = currentOrderData.value;
      const tempCustomFieldOrderData: CustomFieldOrderData = {};
      //Next iterate through customfields, customfields_perticket and customfields_pertickettype to setup currentOrderData.customfields_data
      //Afterwards crosscheck with existing currentOrderData.customfields_data to retain existing values
      //Custom fields
      customfields.value.custom_fields.forEach(field => {
        const customFieldType = getCustomFieldFieldType(field, null, customfields.value.fieldlists || {});
        const fieldListID = getFieldlistID(field, null);
        let fieldlist: FieldListItem | null = null;
        if((customFieldType == 'dropdown' || customFieldType == 'textfield') && fieldListID != undefined && customfields.value.fieldlists && customfields.value.fieldlists[fieldListID.toString()]) {
          fieldlist = customfields.value.fieldlists[fieldListID.toString()];
        }
        const tempCustomField: CustomFieldOrderDataEntry = {
            eventid: 'per_event',
            value: '',
            'must-be-checked': field.must_be_checked,
            customFieldData: field,
            name: field.name,
            type: 'per_event',
            fieldType: customFieldType,
            fieldlist: fieldlist,
            fieldlistID: fieldListID ?? null,
            grouping: 'event'
        };
        if( tempOrderData.customfields_data && tempOrderData.customfields_data[tempCustomField.eventid] && tempOrderData.customfields_data[tempCustomField.eventid][field.name] ) {
          console.log('Retaining value for custom field', field.name, ':', tempOrderData.customfields_data[tempCustomField.eventid][field.name].value);
          tempCustomField.value = tempOrderData.customfields_data[tempCustomField.eventid][field.name].value;
          console.log('Value for custom field', field.name, ':', tempCustomField.value);
        }
        if(tempCustomField.value == '' && customFieldType == 'checkbox'){
          tempCustomField.value = false;
        }
        if( !tempCustomFieldOrderData[tempCustomField.eventid] ) {
          tempCustomFieldOrderData[tempCustomField.eventid] = {};
        }
        console.log('Setting custom field for event', tempCustomField.eventid, 'with name', field.name, ':', tempCustomField);
        tempCustomFieldOrderData[tempCustomField.eventid][field.name] = tempCustomField;
      });
      //For each event in basket we must setup per ticket and per tickettype customfields
      // console.log('Basket for custom fields:', basket.basket);
      const customfields_ticket_count: Record<string, number> = {};
      basket.basket.forEach(basketItem => {
        //... and now through tickets
        // console.log('Basket item for custom fields:', basketItem);
        basketItem.tickets.forEach(ticket => {
          //Per tickettype fields
          customfields.value.custom_fields_pertickettype.forEach(field => {
            if( !field.event_id || field.event_id.toString() != basketItem.id.toString() ) {
              return;
            }
            if(field.tickets && field.tickets.length > 0) {
              const tempTicketId = field.tickets.find((id: number) => id == ticket.id); // for some reason include did not work.
              if(!tempTicketId) {
                return;
              }
            }
            const tempCustomFieldName = 'TT' + ticket.id + '_' + field.name;
            const customFieldType = getCustomFieldFieldType(field, ticket, customfields.value.fieldlists || {});
            const fieldListID = getFieldlistID(field, ticket);
            let fieldlist: FieldListItem | null = null;
            console.log('Setting up per tickettype custom field', field.name, 'for ticket', ticket.id, 'with custom field type', customFieldType, 'and fieldListID', fieldListID);
            console.log('Available fieldlists:', customfields.value.fieldlists);
            if((customFieldType == 'dropdown' || customFieldType == 'textfield') && fieldListID != undefined && customfields.value.fieldlists && customfields.value.fieldlists[fieldListID.toString()]) {
              fieldlist = customfields.value.fieldlists[fieldListID.toString()];
              console.log('Fieldlist found for custom field', field.name, ':', fieldlist);
            }
            const tempCustomField: CustomFieldOrderDataEntry = {
                eventid: field.event_id.toString(),
                ticketID: ticket.id,
                value: '',
                'must-be-checked': field.must_be_checked,
                customFieldData: field,
                name: tempCustomFieldName,
                type: 'per_tickettype',
                fieldType: customFieldType,
                fieldlist: fieldlist,
                fieldlistID: fieldListID ?? null,
                grouping: 'tickettype'
            };
            if( tempOrderData.customfields_data && tempOrderData.customfields_data[tempCustomField.eventid] && tempOrderData.customfields_data[tempCustomField.eventid][tempCustomFieldName] ) {
              tempCustomField.value = tempOrderData.customfields_data[tempCustomField.eventid][tempCustomFieldName].value;
            }
            if(tempCustomField.value == '' && customFieldType == 'checkbox'){
              tempCustomField.value = false;
            }
            if( !tempCustomFieldOrderData[tempCustomField.eventid] ) {
              tempCustomFieldOrderData[tempCustomField.eventid] = {};
            }
            console.log('Setting custom field for event', tempCustomField.eventid, 'with name', tempCustomFieldName, ':', tempCustomField);
            tempCustomFieldOrderData[tempCustomField.eventid][tempCustomFieldName] = tempCustomField;
          });

          //Per ticket fields
          customfields.value.custom_fields_perticket.forEach(field => {
            if( !field.event_id || field.event_id.toString() != basketItem.id.toString() ) {
              return;
            }
            if(field.tickets && field.tickets.length > 0) {
              const tempTicketId = field.tickets.find((id: number) => id == ticket.id);
              if(!tempTicketId) {
                return;
              }
            }
            for(let i = 0; i < ticket.count; i++ ) {
              if(!customfields_ticket_count[field.name.toString()]){
                customfields_ticket_count[field.name.toString()] = 0;
              }
              const tempCustomFieldName = 'T' + customfields_ticket_count[field.name.toString()] + '_' + field.name;
              customfields_ticket_count[field.name.toString()]++;
              const customFieldType = getCustomFieldFieldType(field, ticket, customfields.value.fieldlists || {});
              const fieldListID = getFieldlistID(field, ticket);
              let fieldlist: FieldListItem | null = null;
              if((customFieldType == 'dropdown' || customFieldType == 'textfield') && fieldListID != undefined && customfields.value.fieldlists && customfields.value.fieldlists[fieldListID.toString()]) {
                fieldlist = customfields.value.fieldlists[fieldListID.toString()];
              }
              const actual_iteration = i + 1;
              const tempCustomField: CustomFieldOrderDataEntry = {
                  eventid: field.event_id.toString(),
                  ticketID: ticket.id,
                  value: '',
                  'must-be-checked': field.must_be_checked,
                  customFieldData: field,
                  name: tempCustomFieldName,
                  type: 'per_ticket',
                  fieldType: customFieldType,
                  fieldlist: fieldlist,
                  fieldlistID: fieldListID ?? null,
                  grouping: '#' + actual_iteration
              };
              if( tempOrderData.customfields_data && tempOrderData.customfields_data[tempCustomField.eventid] && tempOrderData.customfields_data[tempCustomField.eventid][tempCustomFieldName] ) {
                tempCustomField.value = tempOrderData.customfields_data[tempCustomField.eventid][tempCustomFieldName].value;
              }
              if(tempCustomField.value == '' && customFieldType == 'checkbox'){
                tempCustomField.value = false;
              }
              if( !tempCustomFieldOrderData[tempCustomField.eventid] ) {
                tempCustomFieldOrderData[tempCustomField.eventid] = {};
              }
              console.log('Setting custom field for event', tempCustomField.eventid, 'with name', tempCustomFieldName, ':', tempCustomField);
              tempCustomFieldOrderData[tempCustomField.eventid][tempCustomFieldName] = tempCustomField;
            }
          });
        });
      });
      currentOrderData.value.customfields_data = tempCustomFieldOrderData;


    }
    console.log('Custom field data set:', currentOrderData.value.customfields_data);
  }

  function getCustomFieldFieldType(customField: CustomField, ticket: Ticket | null, fieldLists: Record<string, FieldListItem>): string {
    let fieldtype = "textfield";
    if(customField.datefield) {
      fieldtype = "datefield";
    }
    else if(customField.text_large) {
      fieldtype = "textarea";
    }
    else if(customField.checkbox) {
      fieldtype = "checkbox";
    }
    else if(customField.cprnumber) {
      fieldtype = "cprnumber";
    }
    else if(customField.fieldlist != undefined ) {
      const fieldListID = getFieldlistID( customField, ticket );

      if( fieldLists && fieldListID != undefined && fieldLists[fieldListID.toString()] && fieldLists[fieldListID.toString()].dropdown ) {
        fieldtype = "dropdown";
      }
    }
    return fieldtype;
  }

  function getFieldlistID(customField: CustomField, ticket: Ticket | null): number | undefined {
    let fieldlistID: number | number[] | undefined = undefined;
    if( customField.fieldlist != undefined ) {
      if( typeof customField.fieldlist === 'number' || ticket == null ) {
        fieldlistID = customField.fieldlist;
      }
      else if(ticket != null ){
        fieldlistID = customField.fieldlist[ticket.id];
      }
    }
    return fieldlistID as number | undefined;
  }

  function setBasketData(data: BasketData) {
    //iterate through basketData.basket and fill in image, venuename and date from events in general store
    basketData = data;
    const startExpireBasket = addEventDataToBasket();
    //We want to reset the cart expiration timer only if we are not in receipt mode
    if(!receiptCart.value && !paymentFailedCart.value && startExpireBasket) {
      if (cartExpireInterval) clearInterval(cartExpireInterval);
      cartExpireInterval = window.setInterval(() => {
        const now = Date.now() / 1000;
        if (basketData.expire && basketData.expire > 0 && now > basketData.expire) {
          doBasketExpireTimeout();
        }
      }, 1000);
      cartExpire.value = basketData.expire ?? 0;
      cardTypes.value = basketData.cardtypes;
      console.log('Cart expire set to:', cartExpire.value);
    }
    else {
      // In receipt mode, payment failed mode, or no tickets reserved, do not start expiration timer and clear it
      if (cartExpireInterval) {
        clearInterval(cartExpireInterval);
        cartExpireInterval = undefined;
      }
      cartExpire.value = 0;
    }

    // console.log('Basket data set:', basketData);
    // console.log('Basket items:', basketData.basket);
  }

  function doBasketExpireTimeout() {
    //clear basket, close connection, goto frontpage
    // console.log('Cart expired, emptying cart and redirecting to frontpage');
    localStorage.setItem('showEmptiedCartToast', 'true');
    clearStoredCartData();
    currentOrderData.value = emptyOrderData;
    basketData.basket = [];
    items.splice(0, items.length);
    cartExpire.value = 0;
    if (cartExpireInterval) clearInterval(cartExpireInterval);
    cartExpireInterval = undefined;
    emptyCart(false);
    websocketStore.close();
    window.location.href = '/';
  }

  /*function getBasketData() {
    return basketData;
  }*/

  function addEventDataToBasket() {
    const basket: CartItem[] = [];
    let startExpireBasket = false;
    basketData.basket.forEach(item => {
      const event = generalStore.events().find(event => event.id == item.id);
      // console.log(general);
      const cartItem: CartItem = item;
      // console.log('Event found for item:', event);
      if (event) {
        const venues = generalStore.venues();
        const venueObj = venues.find(v => v.id === event.venue);
        const venueName = venueObj ? venueObj.name : '';
        let date = formatDate(getEventtime(event) || 0, { showTime: true });
        if (event.eventtime_override) {
          date = event.eventtime_override;
        }
        cartItem.image = photoToUrl(event.photo);
        cartItem.venueName = venueName;
        cartItem.date = date;
        cartItem.itemTimestamp = getEventtime(event) || 0;
        cartItem.itemTimestampOverride = event.eventtime_override || null;
        // console.log('item set:', cartItem);
        basket.push(cartItem);

        if( item.tickets.some(ticket => ticket.count > 0) ) {
          startExpireBasket = true;
        }
      }
    });
    //If in checkout/tickets and basket is empty we must add the checkoutID from checkoutStore
    if(router.currentRoute.value.name === 'checkout-tickets' && basket.length === 0 ) {
      const checkoutID = checkoutStore.getCheckoutID();
      if(checkoutID) {
        const events = generalStore.events();
        const item = events.find(item => item.id === checkoutID);
        if (item && !basket.some(cartItem => cartItem.id === item.id)) {
          basket.push(eventToCartItem(item));
        }
      }
    }


    basketData.basket = basket;
    return startExpireBasket
  }

  function setReceiptMessage(data: ReceiptMessage) {
    receiptMessage.value = data;
    paymentConfirmed.value = receiptMessage.value.ok;
    calculateTotals();
    console.log('Receipt message set:', receiptMessage);
  }
  function setReceiptOrder(data: Order) {
    receiptOrder.value = data;
    pdfLink.value = receiptOrder.value.mailid && receiptOrder.value.mailid.length > 0 ? '/pdf?id=' + receiptOrder.value.orderid + '&mailid=' + receiptOrder.value.mailid : '#';
    if( receiptOrder.value.basket ) {
      receiptCart.value = true;
      basketData.basket = receiptOrder.value.basket;
      addEventDataToBasket();
      items.splice(0, items.length, ...basketData.basket);
      console.log('Items after update:', items);
      calculateTotals();
      console.log('Cart totals after update:', cartTotals.value);
      setReceiptURL();

      if(data.ts){
        receiptMessage.value.ts = data.ts;
      }
    }
    currentOrderData.value = emptyOrderData;
    clearUnpaidOrderData();
    clearStoredCartData();
    console.log('Receipt order set:', receiptOrder);
  }

  function setAPIReceipt(data: APIReceipt) {
    console.log('API Receipt begin:');
    receiptCart.value = true;

    function execute() {
      const tempReceiptMessage: ReceiptMessage = {
        cardnumber: data.cardnumber,
        currency: data.currency,
        fee: data.fee,
        giftcards: data.giftcards,
        mobile_pay_info: '',
        ok: data.status === 'ok',
        orderid: data.orderid,
        transaction: data.transaction,
        ts: data.ts,
        ts_gmt_offset: data.ts_gmt_offset,
        passbook: data.passbook ?? emptyPassbookData
      };
      setReceiptMessage(tempReceiptMessage);
      const tempReceiptOrder: Order = {
        status: data.status,
        orderid: data.orderid,
        ts: data.ts,
        ts_gmt_offset: data.ts_gmt_offset,
        mailid: data.mailid,
        basket: data.basket,
        info: data.info,
        payment_id: '',
        payment_vars: {},
        dankort_discount: data.dankort_discount,
        giftcards: data.giftcards,
        additional_receipt_message: '',
      };
      setReceiptOrder(tempReceiptOrder);
      pdfReady.value = true;
      console.log('API Receipt set:', data);
    }

    if (!generalStore.loading && generalStore.general !== null) {
      execute();
    } else {
      console.log('API Receipt: waiting for generalStore to finish loading...');
      const stop = watch(
        () => generalStore.loading,
        (loading) => {
          if (!loading && generalStore.general !== null) {
            stop();
            execute();
          }
        }
      );
    }
  }

  // Set url to receipt with orderid and mailid
  function setReceiptURL() {
    if( receiptOrder.value.mailid && receiptOrder.value.mailid.length > 0  && receiptOrder.value.orderid > 0 ) {
      history.replaceState({}, '', '/receipt?orderid=' + receiptOrder.value.orderid + '&mailid=' + receiptOrder.value.mailid);
    }
    else {

    }
  }

  const isOpen = ref(false);

  const router = useRouter();

  const open = () => {
    isOpen.value = true;
  };

  const close = () => {
    isOpen.value = false;
  };

  function addToCart(id: number) {
    const events = generalStore.events();
    const item = events.find(item => item.id === id);

    if (item && !items.some(cartItem => cartItem.id === item.id)) {
      items.push(eventToCartItem(item));
    }

    router.push({
      name: 'checkout-tickets',
    });
  }

  function selectAddOns(
    eventId: number,
    addonId: number,
    count: number = 1
  ) {
  const events = generalStore.events();
    const eventItem = events.find(item => item.id === eventId);
    const addon = addonItems?.find(addon => addon.id === addonId);

    if (eventItem && addon) {
      const cartItem = items.find(item => item.id === eventItem.id);

      if (cartItem) {
        const existingAddonIndex = cartItem.addons.findIndex(
          addon => addon.id === addonId
        );
        if (existingAddonIndex !== -1) {
          if (count === 0) {
            cartItem.addons.splice(existingAddonIndex, 1);
          } else {
            cartItem.addons[existingAddonIndex].count = count;
          }
        } else if (count > 0) {
          cartItem.addons.push({
            ...addon,
            count,
          });
        }
      }
    }
  }

  function selectTickets(
    eventId: number | undefined,
    ticketId: number,
    count: number = 1
  ) {
    console.log('Selecting tickets with eventId:', eventId, 'ticketId:', ticketId, 'count:', count);

    const reserveTickets: Record<number, number> = {};
    // console.log('eventItem:', eventItem);
    let foundTicket = false;

    items.forEach(item => {
      item.tickets.forEach(ticket => {
        if (ticket.count > 0) {
          if( ticket.id === ticketId ) {
            ticket.count = count;
            foundTicket = true;
          }
          reserveTickets[ticket.id] = ticket.count;
        }
      });
    });
    if( !foundTicket ) {
      reserveTickets[ticketId] = count;
    }
    // console.log('Current reserved tickets before update:', reserveTickets);

    /* Remove any tickets with count 0 */

    for (const ticketId in reserveTickets) {
      if (reserveTickets[ticketId] == 0) {
        delete reserveTickets[ticketId];
      }
    }

    // // console.log('Reserve tickets flag:', reserveTickets);
    if( Object.keys(reserveTickets).length > 0 ) {
      websocketStore.send('reservetickets', { tickets: reserveTickets }, undefined);
    }
    else {
      emptyCart(true);
    }
    // console.log('Cart items after selecting tickets:', items);
  }

  function setSimpleBasketData(data: SimpleBasketData) {
    //Make basketData from SimpleBasketData
    //Then set via updateReservedTickets
    if(data.timeout !== undefined && data.timeout == 1 ) {
      doBasketExpireTimeout();
    }
    else if(!receiptCart.value && !paymentFailedCart.value && !router.currentRoute.value.name?.toString().startsWith('checkout-receipt')) {
      console.log('Setting simple basket data:', data);
      console.log('receiiptCart.value:', receiptCart.value);
      console.log('paymentFailedCart.value:', paymentFailedCart.value);
      console.log('Current route:', router.currentRoute.value.name);
      basketData.expire = data.expire;
      basketData.basket = data.basket;
      updateReservedTickets(basketData);
    }
    else {
      console.log("Do nothing?");
      console.log('receiiptCart.value:', receiptCart.value);
      console.log('paymentFailedCart.value:', paymentFailedCart.value);
      console.log('Current route:', router.currentRoute.value.name);
    }
  }

  function updateReservedTickets(messageData: BasketData) {
    setCustomFieldData(messageData);
    setBasketData(messageData);
    // console.log('Items before update:', items);
    items.splice(0, items.length, ...basketData.basket);
    console.log('Items after update:', items);
    calculateTotals();
    updateCurrentCount();
    if( messageData.different_count_message ) {
      differentCountMessage.value = messageData.different_count_message;
    }
    else {
      differentCountMessage.value = { message: 'initial', count: 0 };
    }
    console.log('Cart totals after update:', cartTotals.value);
    storeCartData();
  }

  function resendReservedTickets() {
    const reserveTickets: Record<number, number> = {};
    items.forEach(item => {
      item.tickets.forEach(ticket => {
        if (ticket.count > 0) {
          reserveTickets[ticket.id] = ticket.count;
        }
      });
    });
    console.log('Resending reserved tickets:', reserveTickets);
    websocketStore.send('reservetickets', { tickets: reserveTickets }, undefined);
  }

  function calculateTotals() {
    const totals = {
      total: 0,
      fee: 0,
      vat: 0,
      price: 0,
      discount: 0,
      transaction_fee: 0,
    };

    let ticketprice_vat_percentage = 0;
    let ticketfee_vat_percentage = 0;
    let cardfee_vat_percentage = 0;

    items.forEach(item => {
      //get PG info here for seatmap - do later, see if we cannot do it more efficiently
      //current function in SFTNG.Ticketsale.Basket.js - populate_ticketsale
      ticketprice_vat_percentage = (1 - (1 / (1 +item.vat.ticketprice / 100)));
      ticketfee_vat_percentage = (1 - (1 / (1 +item.vat.ticketfee / 100)));
      cardfee_vat_percentage = (1 - (1 / (1 +item.vat.cardfee / 100)));
      item.tickets.forEach(ticket => {
        const ticketfee_extra = ticket.hide_ticketfee_extra
          ? 0
          : ticket.price * ticket.count * ticket.ticketfee_extra;
        const ticketfee = ticket.ticketfee * ticket.count;
        const ticketprice = ticket.price * ticket.count;
        const vat_ticketprice = ticketprice_vat_percentage * ticketprice;
        const vat_ticketfee = ticketfee_vat_percentage * (ticketfee + ticketfee_extra);
        totals.vat += vat_ticketprice + vat_ticketfee;
        totals.price += ticketprice;
        totals.total += ticketprice + ticketfee + ticketfee_extra;
        totals.fee += ticketfee + ticketfee_extra;
      });

      item.addons?.forEach(addon => {
        totals.total += addon.price * addon.count;
      });
    });

    // We might want to use this.
    /*if(basketData.dankort_discount && basketData.dankort_discount > 0 ) {
      totals.discount = basketData.dankort_discount;
      totals.total -= basketData.dankort_discount;
    }
    else */
    if(receiptOrder.value && receiptOrder.value.dankort_discount && receiptOrder.value.dankort_discount > 0 ) {
      totals.discount = receiptOrder.value.dankort_discount;
      totals.total -= receiptOrder.value.dankort_discount;
    }

    if(receiptMessage.value && receiptMessage.value.fee) {
      totals.transaction_fee = receiptMessage.value.fee;
      totals.total += receiptMessage.value.fee;
    }

    const vat_transaction_fee = totals.transaction_fee * cardfee_vat_percentage;
    totals.vat += vat_transaction_fee;

    const vat_discount = totals.discount * ticketfee_vat_percentage;
    totals.vat -= vat_discount;

    cartTotals.value.total = totals.total;
    cartTotals.value.total_price = totals.price;
    cartTotals.value.total_ticketfee = totals.fee;
    cartTotals.value.total_vat = totals.vat;
    cartTotals.value.total_discount = totals.discount;
    cartTotals.value.total_transaction_fee = totals.transaction_fee;
    console.log('Calculated totals:', cartTotals.value);
  }

  //Compare the count in basketData with currentCount and update currentCount with new count. If in checkout we want to trigger differentCount warning (Warning is in vue file, boolean flag here)
  function updateCurrentCount(){
    const newCount: Record<string, number> = {};
    differentCount.value = false;
    differentCountCalculating.value = true;
    items.forEach(item => {
      item.tickets.forEach(ticket => {
        newCount[ticket.id] = ticket.count;
        if( currentCount.value[ticket.id] != undefined && currentCount.value[ticket.id] != ticket.count ) {
          if(currentCount.value[ticket.id] != 0) {
            differentCount.value = true;
          }
          currentCount.value[ticket.id] = ticket.count;
        }
      });
    });
    //If currentCount has any keys that are not in newCount we must set them to 0
    for( const ticketId in currentCount.value ) {
      if( newCount[ticketId] == undefined ) {
        if( currentCount.value[ticketId] != 0 ) {
          differentCount.value = true;
        }
        currentCount.value[ticketId] = 0;
      }
    }
    //if we are not on checkout-tickets page we want to reset differentCount to false, since we only want to show the warning on checkout-tickets page
    if( !router.currentRoute.value.name?.toString().startsWith('checkout-tickets') ) {
      differentCount.value = false;
    }
    //Set differentCountCalculating to false after a short delay to ensure that the warning is shown if there is a different count
    setTimeout(() => {
      differentCountCalculating.value = false;
    }, 250);

  }

  function emptyCart(excemptActiveEventID: boolean = false) {
    const exemptID = excemptActiveEventID ? checkoutStore.getCheckoutID() : null;
    for( const ticketId in currentCount.value ) {
      currentCount.value[ticketId] = 0;
    }
    if (exemptID) {
      for (let i = items.length - 1; i >= 0; i--) {
        if (items[i].id != exemptID) {
          items.splice(i, 1);
        }
      }
    } 
    else {
      items.splice(0, items.length);
    }
    differentCount.value = false;
    //clearStoredCartData();
    websocketStore.send('emptybasket', {  }, undefined);
  }

  function removeFromCart(id: number, resend: boolean = true) {
    const index = items.findIndex(item => item.id === id);
    const eventId = items[index].id;
    items[index].tickets.forEach(ticket => {
      currentCount.value[ticket.id] = 0;
    });
    if (index !== -1) {
      items.splice(index, 1);
    }
    if (resend) {
      if( items.length == 0 ) {
        emptyCart(false);
      }
      else {
        websocketStore.send('subscribeevent', { id: eventId }, undefined);
        resendReservedTickets();
      }
    }
  }

  function gotoTicketReservation(id: number) {
    const index = items.findIndex(item => item.id === id);
    const eventId = items[index].id;
    checkoutStore.setCheckoutID(eventId);
    router.push({
      name: 'checkout-tickets',
    });
    close();
  }

  function cleanUpEmptyEvents(resend: boolean = true) {
    for (let i = items.length - 1; i >= 0; i--) {
      const item = items[i];
      const hasTickets = item.tickets.some(ticket => ticket.count > 0);
      if (!hasTickets) {
        removeFromCart(item.id, resend);
      }
    }
  }

  function resetCart() {
    console.log('Resetting cart');
    items.splice(0, items.length);
    cartTotals.value = { total: 0, total_ticketfee: 0, total_price: 0, total_vat: 0, total_discount: 0, total_transaction_fee: 0 };
    cartExpire.value = 0;
    cardTypes.value = [];
    receiptCart.value = false;
    if (cartExpireInterval) {
      clearInterval(cartExpireInterval);
      cartExpireInterval = undefined;
    }
    basketData = {
      basket: [],
      cardtypes: [],
      dankort_discount: 0,
      dankort_discount_max: 0,
      dankort_discount_min: 0,
      error: '',
      different_count_message: undefined,
      expire: 0,
      volume_discounts_in_use: undefined,
      customfields: undefined,
      timeout: undefined,
    };
    currentOrderData.value = emptyOrderData;
    customfields.value = emptyCustomFieldData;
    receiptOrder.value = emptyReceiptOrder;
    receiptMessage.value = emptyReceiptMessage;
    checkoutStore.clearCheckoutData();
    paymentConfirmed.value = false;
    pdfReady.value = false;
    pdfLink.value = '#';
    clearUnpaidOrderData();
    clearStoredCartData();
  }

  function isCartEmpty() {
    return items.length === 0;
  }

  function updateCustomFieldsStartPurchase(data: StartPurchaseData) {
    // console.log('Updating custom fields from start purchase data:', data);
    basketData.customfields = emptyCustomFieldData;
    basketData.customfields.custom_fields = data.custom_fields ? data.custom_fields : [];
    basketData.customfields.custom_fields_perticket = data.custom_fields_perticket ? data.custom_fields_perticket : [];
    basketData.customfields.custom_fields_pertickettype = data.custom_fields_pertickettype ? data.custom_fields_pertickettype : [];
    basketData.customfields.fieldlists = data.fieldlists ? data.fieldlists : {};
    // console.log('Basket data custom fields after start purchase update:', basketData.customfields);
    setCustomFieldData(basketData);
    // console.log('Custom fields after start purchase update:', customfields.value);
    // console.log('Current order data after start purchase update:', currentOrderData.value);
  }



  watch(currentOrderData, () => {
    storeCartData();
  }, { deep: true });

  //Function to store relevant cart data to localstorage
  function storeCartData() {
    const cartData = {
      basketData: basketData,
      currentOrderData: currentOrderData.value,
      customfields: customfields.value,
      ts: Date.now(),
    };
    localStorage.setItem('cartData', JSON.stringify(cartData));
    console.log('Cart data stored to localStorage:', cartData);
  }

  function loadCartData(do_cleanup: boolean = true) {
    const cartDataString = localStorage.getItem('cartData');
    if( cartDataString ) {
      const cartData = JSON.parse(cartDataString);
      //We only load the data if it is less than 20 minutes old. We don't want to store data longer than necessary.
      if( cartData.ts && Date.now() - cartData.ts > 20 * 60) {
        if( cartData.basketData ) {
          if(!receiptCart.value || !router.currentRoute.value.name?.toString().startsWith('checkout-receipt')) {
            basketData = cartData.basketData;
            setBasketData(basketData);
            if(receiptCart.value) {
              receiptCart.value = false;
            }
          }
          if( do_cleanup ) {
            console.log('Cleaning up empty events from loaded cart data');
            cleanUpEmptyEvents(false);
          }
          items.splice(0, items.length, ...basketData.basket);
        }
        if( cartData.currentOrderData ) {
          console.log('Loading current order data from localStorage:', cartData.currentOrderData);
          currentOrderData.value = cartData.currentOrderData;
          console.log('Current order data loaded from localStorage:', currentOrderData.value);
        }
        if( cartData.customfields ) {
          customfields.value = cartData.customfields;
        }
      }
    }
    console.log('basketData after load:', basketData);
    console.log('currentOrderData after load:', currentOrderData.value);
    console.log('customfields after load:', customfields.value);
    clearStoredCartData();
  }

  function clearStoredCartData() {
    localStorage.removeItem('cartData');
  }

  return {
    addToCart,
    removeFromCart,
    gotoTicketReservation,
    items,
    selectAddOns,
    selectTickets,
    updateReservedTickets,
    resendReservedTickets,
    isOpen,
    open,
    close,
    emptyCart,
    cleanUpEmptyEvents,
    cartExpire,
    cartTotals,
    cardTypes,
    receiptCart,
    resetCart,
    setReceiptMessage,
    setSimpleBasketData,
    setReceiptOrder,
    receiptOrder,
    receiptMessage,
    receiptMessagesSent,
    paymentConfirmed,
    pdfReady,
    pdfLink,
    customfields,
    currentOrderData,
    setCustomFieldRule,
    getCustomFieldRule,
    setAPIReceipt,
    setReceiptURL,
    isCartEmpty,
    updateCustomFieldsStartPurchase,
    storeCartData,
    loadCartData,
    clearStoredCartData,
    currentCount,
    differentCount,
    differentCountCalculating,
    differentCountMessage,
    setUnpaidOrder,
    doUnpaidOrderTimeout,
    unpaidOrder,
    paymentFailedTimeoutFlag,
    paymentFailedExpire,
    paymentFailedAvailable,
    doBackupUnpaidOrder,
    ignoreCancelPaymentModal,
    firstBasketLoad
  };
});
